import Konva from "konva";
import consts, { TypeCanvasElement } from "shared/consts";
import * as ConnectorUtils from "frontend/canvas-designer-new/elements/connector/connector-utils";
import * as AnchorUtils from "frontend/canvas-designer-new/elements/connector/anchor-point-utils";
import Transform, { Degrees } from "utils/transform";
import normalAt from "frontend/geometry/normalAt";
import pointAt from "frontend/geometry/pointAt";
import { Point } from "shared/datamodel/schemas/canvas-element";
import { parseStrokeWidth } from "shared/util/utils";
import { computeConnectorDrawingData } from "./connector-drawing-utils";
import { StandardAnchorPoints } from "frontend/canvas-designer-new/elements/connector/anchor-point-utils";

// Enums for connector movement logic
enum ConnectorType {
  SELECTED = "selected",
  NON_SELECTED = "non-selected",
}

enum ConnectionState {
  FREE_FLOATING = "free-floating",
  ALL_MOVING = "all-moving",
  PARTIAL_MOVING = "partial-moving",
}

enum ConnectorAction {
  DO_NOTHING = "do-nothing",
  MOVE_ENTIRELY = "move-entirely",
  RECALCULATE_ANCHORS = "recalculate-anchors",
}

// Helper function to move entire connector
function moveEntireConnector(connectorNode: Konva.Node, connectorElement: any, deltaX: number, deltaY: number): void {
  connectorNode.x(connectorNode.x() + deltaX);
  connectorNode.y(connectorNode.y() + deltaY);

  const updatedElement = {
    ...connectorElement,
    x: connectorElement.x + deltaX,
    y: connectorElement.y + deltaY,
  };
  connectorNode.setAttrs({ element: updatedElement });
}

function findAllConnectedConnectors(movingElementIds: Set<string>, layer: Konva.Layer): Set<string> {
  const connectedConnectorIds = new Set<string>();

  // Get all nodes from the layer and filter for connectors
  const allNodes = layer.find(() => true).toArray();
  const connectorNodes = allNodes.filter((node) => node.attrs.type === consts.CANVAS_ELEMENTS.CONNECTOR);

  for (const connectorNode of connectorNodes) {
    const connectorElement = connectorNode.attrs.element;
    const connectedShapes = connectorElement.connectedShapes || [];

    // Check if this connector is connected to any moving element
    const isConnected = connectedShapes.some((shape: any) => shape?.id && movingElementIds.has(shape.id));

    if (isConnected) {
      connectedConnectorIds.add(connectorNode.id());
    }
  }

  return connectedConnectorIds;
}

// Enhanced function to get all attached connectors for moving elements
function getAllAttachedConnectors(movingNodes: Konva.Node[], layer: Konva.Layer): Set<string> {
  const allConnectorIds = new Set<string>();
  const movingElementIds = new Set(movingNodes.map((node) => node.id()));

  // First, try the fast approach using attachedConnectors attribute
  for (const movingNode of movingNodes) {
    const attachedConnectors = movingNode.attrs.element?.attachedConnectors || movingNode.attrs.attachedConnectors;

    if (attachedConnectors) {
      for (const connectorId of Object.keys(attachedConnectors)) {
        allConnectorIds.add(connectorId);
      }
    }
  }

  // Then, use the comprehensive search to catch any connectors missed by the fast approach
  // This is especially important for noun-icons and other elements without attachedConnectors
  const additionalConnectors = findAllConnectedConnectors(movingElementIds, layer);
  for (const connectorId of additionalConnectors) {
    allConnectorIds.add(connectorId);
  }

  return allConnectorIds;
}

// Helper function to calculate anchor position for a specific index
function calculateAnchorPosition(
  index: number,
  connectedShapes: any[],
  connectorElement: any,
  movingElementIds: Set<string>,
  layer: Konva.Layer
): ConnectorUtils.ConnectorEndpoint {
  if (!connectedShapes[index]?.id) {
    // For free-floating points, keep them exactly where they are (no movement)
    const originalPoint = connectorElement.points[index];

    return {
      x: originalPoint.x,
      y: originalPoint.y,
      rotation: originalPoint.rotation,
    };
  }

  // For attached connectors, get the current shape data and actual node position
  const shapeId = connectedShapes[index]?.id;
  if (!shapeId) {
    return connectorElement.points[index];
  }

  const connectedShapeNode = layer.findOne(`#${shapeId}`);
  const shape = connectedShapeNode?.attrs?.element;
  if (!shape) {
    return connectorElement.points[index];
  }

  const mode = ConnectorUtils.fixAnchorsMode(connectorElement.anchorsMode?.[index]);
  if (mode === "n/a") {
    return connectorElement.points[index];
  }

  let anchorPoints: StandardAnchorPoints | null = null;

  if (shape.type === "noun-icon" || shape.type === "image") {
    if (movingElementIds.has(shapeId)) {
      // Get the node-based anchors using the original position
      const originalNodeAnchors = AnchorUtils.getAnchorPointsFromNode(connectedShapeNode);

      if (originalNodeAnchors) {
        // Calculate the delta between original and current position
        const currentX = connectedShapeNode.x();
        const currentY = connectedShapeNode.y();
        const deltaX = currentX - shape.x;
        const deltaY = currentY - shape.y;

        // Apply the delta to all anchor points
        anchorPoints = {} as StandardAnchorPoints;
        for (const [key, anchor] of Object.entries(originalNodeAnchors)) {
          if (key in anchorPoints || Object.prototype.hasOwnProperty.call(originalNodeAnchors, key)) {
            (anchorPoints as Record<string, typeof anchor>)[key] = {
              ...anchor,
              x: anchor.x + deltaX,
              y: anchor.y + deltaY,
            };
          }
        }
      } else {
        return connectorElement.points[index];
      }
    } else {
      // For non-moving noun-icons, use node-based anchors as before
      anchorPoints = AnchorUtils.getAnchorPointsFromNode(connectedShapeNode);
    }
  } else {
    // For non-noun-icon elements, use the original data-based logic
    let currentShapeData = shape;
    if (movingElementIds.has(shapeId) && connectedShapeNode) {
      currentShapeData = {
        ...shape,
        x: connectedShapeNode.x(),
        y: connectedShapeNode.y(),
      };
    }

    anchorPoints = AnchorUtils.getAnchorPoints(shape.type as TypeCanvasElement, currentShapeData);
  }

  if (!anchorPoints) {
    return connectorElement.points[index];
  }

  // Calculate the anchor position based on mode
  if (mode === "outline") {
    // For outline mode, we need the current shape data
    let currentShapeData = shape;
    if (movingElementIds.has(shapeId)) {
      currentShapeData = {
        ...shape,
        x: connectedShapeNode.x(),
        y: connectedShapeNode.y(),
      };
    }

    const _t = index === 0 ? connectorElement.point1_t : connectorElement.point2_t;
    // Determine the correct canvas element type
    let canvasElementType: TypeCanvasElement;
    // Map element types to correct canvas element types
    switch (currentShapeData.type) {
      // Shape subtypes map to "shape"
      case "circle":
      case "rect":
      case "rect-rounded":
      case "diamond":
      case "triangle":
      case "hexagon": {
        canvasElementType = consts.CANVAS_ELEMENTS.SHAPE;
        break;
      }

      // Image elements map to "file"
      case "image": {
        canvasElementType = consts.CANVAS_ELEMENTS.FILE;
        break;
      }

      // Other elements use their type directly (if it's a valid canvas element type)
      default: {
        canvasElementType = currentShapeData.type as TypeCanvasElement;
        break;
      }
    }
    const rotation = normalAt(canvasElementType, currentShapeData)(_t);
    const outlinePoint = pointAt(canvasElementType, currentShapeData)(_t);

    const xy = {
      ...ConnectorUtils.canvasPointToElementPoint(connectorElement, outlinePoint),
      rotation: rotation as Degrees,
    };

    return xy;
  } else {
    const anchor = anchorPoints[mode as keyof typeof anchorPoints];
    if (!anchor) {
      return connectorElement.points[index];
    }

    const elementRelativePoint = ConnectorUtils.canvasPointToElementPoint(connectorElement, anchor);

    return {
      x: elementRelativePoint.x,
      y: elementRelativePoint.y,
      rotation: anchor.rotation,
    };
  }
}

// Helper function to validate connector points
function validateConnectorPoints(points: any[], connectorId: string): boolean {
  const isValidPoint = (point: any) =>
    point &&
    typeof point.x === "number" &&
    typeof point.y === "number" &&
    !Number.isNaN(point.x) &&
    !Number.isNaN(point.y) &&
    Number.isFinite(point.x) &&
    Number.isFinite(point.y);

  if (!isValidPoint(points[0]) || !isValidPoint(points[1])) {
    console.warn(`Invalid connector points for ${connectorId}:`, points);
    return false;
  }
  return true;
}

function findNodeByName(node: Konva.Node, targetName: string): Konva.Node | null {
  // Check if current node has the target name
  if (node.name && node.name() === targetName) {
    return node;
  }

  // Search through children recursively
  const children = node.children || [];
  if (children.toArray) {
    for (const child of children.toArray()) {
      const found = findNodeByName(child, targetName);
      if (found) {
        return found;
      }
    }
  }

  return null;
}

// Helper function to update connector text position
function updateConnectorTextPosition(connectorNode: Konva.Node, connectorElement: any, newConnectorData: any): void {
  const textLabelRect = findNodeByName(connectorNode, "connector-text-label");

  if (textLabelRect && connectorElement.text) {
    const textLocation = connectorElement.textLocation || 0.5;
    const metrics = new ConnectorUtils.PathMetrics(newConnectorData);
    const textPosition = metrics.getPointAlongPath(textLocation);

    if (textPosition && textPosition.length >= 2) {
      const { scaleX = 1, scaleY = 1, rotate = 0 } = connectorElement;

      const m = Transform.Point(
        { x: 0, y: 0, width: scaleX, height: scaleY, rotate: rotate as Degrees },
        { x: textPosition[0], y: textPosition[1] }
      );

      // Update text rect position
      textLabelRect.x(m.x);
      textLabelRect.y(m.y);

      // Update text element position
      const textElement = textLabelRect.getParent()?.findOne("Text");
      if (textElement) {
        textElement.x(m.x);
        textElement.y(m.y);
      }

      // Clear any stale clipRect from the shape to ensure fresh calculation
      // This prevents stale clipRect values from being used during updates
      const connectorLineShape = connectorNode.children
        .toArray()
        .find((node: any) => node.name && node.name() === "connector-line");
      if (connectorLineShape?.attrs) {
        // Remove the attrs.clipRect so sceneFunc falls back to the prop-level clipRect
        delete connectorLineShape.attrs.clipRect;
        // Force a redraw to ensure the new clipRect is applied
        connectorLineShape._clearCache();
        connectorLineShape.getLayer()?.batchDraw();
      }
    }
  }
}

// Helper function to update connector visual elements
function updateConnectorVisuals(
  connectorNode: Konva.Node,
  connectorElement: any,
  newConnectorData: any,
  newPoints: ConnectorUtils.ConnectorEndpoint[]
): void {
  const connectorLineShape = connectorNode.children
    .toArray()
    .find((node: any) => node.name && node.name() === "connector-line");

  if (connectorLineShape && connectorLineShape.attrs.connectorData) {
    connectorLineShape.attrs.connectorData.segments = newConnectorData.segments;
  }

  if (connectorLineShape && newConnectorData) {
    connectorLineShape._clearCache();
    connectorLineShape.attrs.connectorData.segments = newConnectorData.segments;
    // Get the connector's scale factors
    const scaleX = connectorNode.attrs.scaleX || 1;
    const scaleY = connectorNode.attrs.scaleY || 1;

    // Helper function to apply scaling
    const scale = (p: Point) => ({
      x: p.x * scaleX,
      y: p.y * scaleY,
    });

    // Calculate arrow positions with scaling applied
    const startPoint = scale(newPoints[0]);
    const endPoint = scale(newPoints[1]);
    const startComingFrom = scale(newConnectorData.secondPoint());
    const endComingFrom = scale(newConnectorData.preFinalPoint());

    // Update arrow head data
    if (connectorLineShape.attrs.startArrowData) {
      connectorLineShape.attrs.startArrowData.position = startPoint;
      connectorLineShape.attrs.startArrowData.comingFrom = startComingFrom;
    }

    if (connectorLineShape.attrs.endArrowData) {
      connectorLineShape.attrs.endArrowData.position = endPoint;
      connectorLineShape.attrs.endArrowData.comingFrom = endComingFrom;
    }

    // Update text position
    updateConnectorTextPosition(connectorNode, connectorElement, newConnectorData);
  }
}

// Helper function to recalculate connector anchors
function recalculateConnectorAnchors(
  connectorNode: Konva.Node,
  connectorElement: any,
  connectedShapes: any[],
  layer: Konva.Layer,
  movingElementIds: Set<string>
): boolean {
  // Remove targetAnchorIndex parameter - we don't need it anymore
  const calcAnchorPosition = (index: number) =>
    calculateAnchorPosition(
      index,
      connectedShapes,
      connectorElement,
      movingElementIds, // Pass the set of moving element IDs
      layer
    );

  const newPoints = [calcAnchorPosition(0), calcAnchorPosition(1)];

  if (!validateConnectorPoints(newPoints, connectorNode.id())) {
    return false;
  }

  // Check if points actually changed
  const pointsChanged =
    Math.abs(newPoints[0].x - connectorElement.points[0].x) > 0.01 ||
    Math.abs(newPoints[0].y - connectorElement.points[0].y) > 0.01 ||
    Math.abs(newPoints[1].x - connectorElement.points[1].x) > 0.01 ||
    Math.abs(newPoints[1].y - connectorElement.points[1].y) > 0.01;

  // ... rest of the function remains the same
  const newConnectorElement = { ...connectorElement, points: newPoints };

  // Get connected shapes for bounding box calculation from Konva nodes
  const startShapeNode = connectedShapes[0]?.id ? layer.findOne(`#${connectedShapes[0].id}`) : null;
  const endShapeNode = connectedShapes[1]?.id ? layer.findOne(`#${connectedShapes[1].id}`) : null;

  const startShape = startShapeNode?.attrs?.element
    ? {
        ...startShapeNode.attrs.element,
        ...(movingElementIds.has(connectedShapes[0]?.id)
          ? {
              x: startShapeNode.x(), // Current visual position
              y: startShapeNode.y(), // Current visual position
            }
          : {}),
      }
    : null;

  const endShape = endShapeNode?.attrs?.element
    ? {
        ...endShapeNode.attrs.element,
        ...(movingElementIds.has(connectedShapes[1]?.id)
          ? {
              x: endShapeNode.x(),
              y: endShapeNode.y(),
            }
          : {}),
      }
    : null;

  const startBbox = ConnectorUtils.getConnectedShapeBbox(
    newConnectorElement,
    newConnectorElement.points[0],
    connectedShapes[0]?.id,
    startShape
  );
  const endBbox = ConnectorUtils.getConnectedShapeBbox(
    newConnectorElement,
    newConnectorElement.points[1],
    connectedShapes[1]?.id,
    endShape
  );

  // Get rotation values
  const startRotate = startShape?.rotate ?? 0;
  const endRotate = endShape?.rotate ?? 0;

  // Prepare element for connector calculation
  const memoizedElement = {
    lineType: newConnectorElement.lineType,
    innerPoints: newConnectorElement.innerPoints,
    strokeWidth: parseStrokeWidth(newConnectorElement.strokeWidth),
    scaleX: newConnectorElement.scaleX,
    scaleY: newConnectorElement.scaleY,
    rotate: newConnectorElement.rotate,
    anchorsMode: newConnectorElement.anchorsMode,
    pointerStyles: newConnectorElement.pointerStyles,
  };

  // Recalculate the connector path with proper curve control points
  const newConnectorData = computeConnectorDrawingData(
    newPoints[0],
    newPoints[1],
    memoizedElement,
    startBbox,
    endBbox,
    startRotate,
    endRotate
  );

  // Update the connector's segments with recalculated curve data for real-time feedback
  if (newConnectorData) {
    updateConnectorVisuals(connectorNode, connectorElement, newConnectorData, newPoints);

    if (!newConnectorData.segments || newConnectorData.segments.length === 0) {
      console.warn(`Invalid connector data for ${connectorNode.id()}:`, newConnectorData);
    }
  }

  // Return whether the connector points actually changed
  return pointsChanged;
}

// Helper function to determine connection state
function getConnectionState(connectedShapes: any[], movingElementIds: Set<string>): ConnectionState {
  const connectedShapeIds = connectedShapes.map((shape: any) => shape?.id).filter(Boolean);

  if (connectedShapeIds.length === 0) {
    return ConnectionState.FREE_FLOATING;
  }

  const allConnectedShapesAreMoving = connectedShapeIds.every((id: string) => movingElementIds.has(id));

  if (allConnectedShapesAreMoving) {
    return ConnectionState.ALL_MOVING;
  }

  return ConnectionState.PARTIAL_MOVING;
}

// Helper function to determine connector type
function getConnectorType(connectorId: string, selectedIds: string[]): ConnectorType {
  return selectedIds.includes(connectorId) ? ConnectorType.SELECTED : ConnectorType.NON_SELECTED;
}

// Helper function to check if a connector is inside a moving frame
function isConnectorInsideMovingFrame(connectorElement: any, movingElementIds: Set<string>): boolean {
  // Check if the connector has a frameId and if that frame is moving
  if (connectorElement.frameId && movingElementIds.has(connectorElement.frameId)) {
    return true;
  }
  return false;
}

// Helper function to check if a connector should be handled by group system
function isConnectorHandledByGroupSystem(
  connectorElement: any,
  connectedShapes: any[],
  movingElementIds: Set<string>,
  layer: Konva.Layer
): boolean {
  // Check if the connector has a groupId
  if (!connectorElement.groupId) {
    return false;
  }

  // Get all elements in the same group
  const groupElements = layer
    .find((node: Konva.Node) => node.attrs.element?.groupId === connectorElement.groupId)
    .toArray();

  // Check if any element in the group is moving
  const movingGroupElements = groupElements.filter((node: Konva.Node) => movingElementIds.has(node.id()));

  if (movingGroupElements.length === 0) {
    return false;
  }

  // Check if MORE THAN ONE element in the group is moving
  const multipleElementsMoving = movingGroupElements.length > 1;

  if (multipleElementsMoving) {
    // When multiple elements in the group are moving, check if connector has external connections
    const connectedShapeIds = connectedShapes.map((shape: any) => shape?.id).filter(Boolean);

    // Check if any connected shape is NOT in the same group or is not moving
    const hasExternalConnections = connectedShapeIds.some((shapeId: string) => {
      const connectedNode = layer.findOne(`#${shapeId}`);
      if (!connectedNode) return false;

      const connectedElement = connectedNode.attrs.element;
      // External if: different group OR same group but not moving
      return connectedElement?.groupId !== connectorElement.groupId || !movingElementIds.has(shapeId);
    });

    if (hasExternalConnections) {
      // Connector has external connections - let connector system handle it
      return false;
    }

    // No external connections - let group system handle ALL connectors
    return true;
  }

  // Only ONE element in the group is moving (individual element movement)
  // If the connector is attached to the moving element,
  // let the connector system handle it for real-time updates during drag
  const connectedShapeIds = connectedShapes.map((shape: any) => shape?.id).filter(Boolean);
  const isAttachedToMovingElement = connectedShapeIds.some((shapeId: string) => movingElementIds.has(shapeId));

  if (isAttachedToMovingElement) {
    return false; // Let connector system handle it for real-time updates
  }

  // If connector is in a moving group but NOT attached to the moving element,
  // let the group system handle it to avoid double movement
  return true;
}

// Helper function to determine action based on decision matrix
function getConnectorAction(
  connectorType: ConnectorType,
  connectionState: ConnectionState,
  connectorElement: any,
  connectedShapes: any[],
  movingElementIds: Set<string>,
  connectorId: string,
  movingNodes: Konva.Node[],
  layer: Konva.Layer
): ConnectorAction {
  // Skip connectors that should be handled by group system
  const isHandledByGroupSystem = isConnectorHandledByGroupSystem(
    connectorElement,
    connectedShapes,
    movingElementIds,
    layer
  );
  if (isHandledByGroupSystem) {
    // Group handles movement completely - connector will move with the group
    return ConnectorAction.DO_NOTHING;
  }

  // Skip for free-floating AND all-moving connectors inside moving frames
  const isInsideMovingFrame = isConnectorInsideMovingFrame(connectorElement, movingElementIds);
  const frameCanHandleConnector =
    connectionState === ConnectionState.FREE_FLOATING || connectionState === ConnectionState.ALL_MOVING;

  if (isInsideMovingFrame && frameCanHandleConnector) {
    // Frame handles movement completely - no external connections to worry about
    return ConnectorAction.DO_NOTHING;
  }

  const connectorIsMoving = movingNodes.some((node) => node.id() === connectorId);
  // Check if selected connector is actually being dragged
  const isSelectedConnectorBeingDragged =
    (connectorType === ConnectorType.SELECTED && connectorIsMoving) || (movingNodes.length === 1 && connectorIsMoving);

  // Decision Matrix Implementation
  if (isSelectedConnectorBeingDragged) {
    // Selected connector is being dragged by user
    switch (connectionState) {
      case ConnectionState.FREE_FLOATING: {
        return ConnectorAction.DO_NOTHING; // Already moved by selection system
      }
      case ConnectionState.ALL_MOVING: {
        return ConnectorAction.DO_NOTHING; // Already moved by selection system
      }
      case ConnectionState.PARTIAL_MOVING: {
        return ConnectorAction.RECALCULATE_ANCHORS; // Need to maintain connections
      }
    }
  } else {
    // Non-selected connector OR selected connector not being dragged
    // Always recalculate anchors to maintain connections when dragging elements
    return ConnectorAction.RECALCULATE_ANCHORS;
  }
}

/**
 * ==================================================================================
 * CONNECTOR MOVEMENT DECISION LOGIC
 * ==================================================================================
 *
 * ┌─────────────────┬─────────────────┬─────────────────┬─────────────────┐
 * │ Connector Type  │ Free-Floating   │ All-Moving      │ Partial-Moving  │
 * │ \ Connection    │ (no connections)│ (all connected  │ (some connected │
 * │  \ State        │                 │  shapes moving) │  shapes NOT     │
 * │   \             │                 │                 │  moving)        │
 * ├─────────────────┼─────────────────┼─────────────────┼─────────────────┤
 * │ SELECTED        │ DO_NOTHING      │ DO_NOTHING      │ RECALCULATE_    │
 * │ (user selected  │ (handled by     │ (handled by     │ ANCHORS         │
 * │  connector AND  │  selection      │  selection      │                 │
 * │  dragging it)   │  system)        │  system)        │                 │
 * ├─────────────────┼─────────────────┼─────────────────┼─────────────────┤
 * │ NON_SELECTED    │ RECALCULATE_    │ RECALCULATE_    │ RECALCULATE_    │
 * │ (connector not  │ ANCHORS         │ ANCHORS         │ ANCHORS         │
 * │  selected OR    │                 │                 │                 │
 * │  not dragging)  │                 │                 │                 │
 * └─────────────────┴─────────────────┴─────────────────┴─────────────────┘
 *
 * CRITICAL: Selected connectors are only handled by selection system when user drags them directly.
 * If user drags a connected element (not the connector), treat as non-selected.
 *
 * Non-selected connectors ALWAYS recalculate anchors to maintain connections.
 * This ensures that when dragging elements, connectors stay anchored and only segments move.
 *
 * SPECIAL CASE: Connectors inside moving frames or groups
 *
 * GROUPS:
 * - If connector.groupId exists AND some elements in the group are moving:
 *   • If MULTIPLE elements are moving:
 *     - If connector has external connections (connected to shapes outside group or not moving) → connector system handles it
 *     - If connector has NO external connections → group system handles it (consistent group behavior)
 *   • If ONLY ONE element is moving:
 *     - If connector IS attached to the moving element → connector system handles it (real-time updates)
 *     - If connector NOT attached to the moving element → group system handles it (prevent double movement)
 * - This ensures external connections are maintained while preserving group consistency for internal connectors
 *
 * FRAMES:
 * - If connector.frameId exists AND frame is moving AND all connected elements are moving → DO_NOTHING
 * - If connector.frameId exists AND frame is moving BUT some connected elements NOT moving → RECALCULATE_ANCHORS
 * - Frame can only handle connectors that don't span outside the frame
 *
 */
export function moveConnectors(
  movingNodes: Konva.Node[],
  deltaX: number,
  deltaY: number,
  layer: Konva.Layer | null,
  selectedIds: string[]
): boolean {
  if (!layer) return false;

  // Track whether any partial-moving connectors changed
  let hasPartialMovingConnectorChanges = false;

  // Also include frames in movingElementIds for frame detection
  const allMovingNodes = movingNodes.filter((node) => {
    const element = node.attrs.element;
    return element?.type !== consts.CANVAS_ELEMENTS.CONNECTOR;
  });

  const selectedConnectorNodes = movingNodes.filter((node) => {
    const element = node.attrs.element;
    return element?.type === consts.CANVAS_ELEMENTS.CONNECTOR;
  });

  // Include ALL non-connector elements (including frames)
  const movingElementIds = new Set(allMovingNodes.map((node) => node.id()));

  // Get connectors attached to non-connector moving elements
  const attachedConnectorIds =
    allMovingNodes.length > 0 ? getAllAttachedConnectors(allMovingNodes, layer) : new Set<string>();

  // Add selected connectors to the processing list
  for (const connectorNode of selectedConnectorNodes) {
    attachedConnectorIds.add(connectorNode.id());
  }

  // Process each unique connector exactly once
  for (const connectorId of attachedConnectorIds) {
    const connectorNode = layer.findOne(`#${connectorId}`);
    if (!connectorNode) continue;

    const connectorElement = connectorNode.attrs.element;
    const connectedShapes = connectorElement.connectedShapes || [];

    // Apply Decision Matrix Logic
    const connectorType = getConnectorType(connectorId, selectedIds);
    const connectionState = getConnectionState(connectedShapes, movingElementIds);
    const action = getConnectorAction(
      connectorType,
      connectionState,
      connectorElement,
      connectedShapes,
      movingElementIds,
      connectorId,
      movingNodes,
      layer
    );

    // Execute the determined action
    switch (action) {
      case ConnectorAction.DO_NOTHING: {
        // Selected connectors that should move entirely are already handled by selection system
        break;
      }

      case ConnectorAction.MOVE_ENTIRELY: {
        // Move non-selected connectors entirely
        moveEntireConnector(connectorNode, connectorElement, deltaX, deltaY);
        break;
      }

      case ConnectorAction.RECALCULATE_ANCHORS: {
        // Recalculate anchors for connectors that are partially moving
        const connectorChanged = recalculateConnectorAnchors(
          connectorNode,
          connectorElement,
          connectedShapes,
          layer,
          movingElementIds
        );
        if (connectorChanged) {
          hasPartialMovingConnectorChanges = true;
        }
        break;
      }
    }
  }

  return hasPartialMovingConnectorChanges;
}
